import React from 'react';
import ReactDOM from 'react-dom';
import Icon from './Icon';
import styles from './Select.module.css';
import inputStyles from './Input.module.css';

// A native <select> can't have a search box inside its dropdown, so this is
// a custom combobox — same portal-to-body + fixed-position + outside-click
// pattern Dropdown.jsx already uses elsewhere in this app (portaled so it
// isn't clipped by an ancestor's `overflow: hidden`, e.g. a table wrapper).
//
// onChange contract: called with the raw selected `value` (not a DOM event,
// since there's no native <select> element to read .target.value off of).
class Select extends React.Component {
  constructor(props) {
    super(props);
    this.state = { open: false, query: '', coords: null, highlighted: 0 };
    this.wrapRef = React.createRef();
    this.searchRef = React.createRef();
    this.menuRef = null;
    this.handleOutside = this.handleOutside.bind(this);
    this.reposition = this.reposition.bind(this);
    this.toggle = this.toggle.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.handleSearchKeyDown = this.handleSearchKeyDown.bind(this);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleOutside);
    window.addEventListener('resize', this.reposition);
    window.addEventListener('scroll', this.reposition, true);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleOutside);
    window.removeEventListener('resize', this.reposition);
    window.removeEventListener('scroll', this.reposition, true);
  }

  handleOutside(e) {
    const insideTrigger = this.wrapRef.current && this.wrapRef.current.contains(e.target);
    const insideMenu = this.menuRef && this.menuRef.contains(e.target);
    if (!insideTrigger && !insideMenu) this.close();
  }

  computeCoords() {
    const rect = this.wrapRef.current.getBoundingClientRect();
    return { top: rect.bottom + 4, left: rect.left, width: rect.width };
  }

  reposition() {
    if (this.state.open) this.setState({ coords: this.computeCoords() });
  }

  toggle() {
    if (this.state.open) { this.close(); return; }
    this.setState({ open: true, coords: this.computeCoords(), query: '', highlighted: 0 });
    // Next tick — the portal (and the input inside it) doesn't exist until
    // this setState's render commits.
    requestAnimationFrame(() => this.searchRef.current && this.searchRef.current.focus());
  }

  close() {
    this.setState({ open: false, query: '' });
  }

  select(option) {
    if (option.disabled) return;
    this.props.onChange(option.value);
    this.close();
  }

  filteredOptions() {
    const { options = [] } = this.props;
    const q = this.state.query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }

  handleSearchChange(e) {
    this.setState({ query: e.target.value, highlighted: 0 });
  }

  handleSearchKeyDown(e) {
    const filtered = this.filteredOptions();
    if (e.key === 'Escape') { this.close(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.setState((s) => ({ highlighted: Math.min(s.highlighted + 1, filtered.length - 1) }));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.setState((s) => ({ highlighted: Math.max(s.highlighted - 1, 0) }));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const option = filtered[this.state.highlighted];
      if (option) this.select(option);
    }
  }

  render() {
    const { label, hint, error, required, options = [], value, placeholder, style, size = 'md' } = this.props;
    const { open, coords, query, highlighted } = this.state;
    const selected = options.find((o) => o.value === value);
    const filtered = this.filteredOptions();

    return (
      <div className={inputStyles.wrap} style={style} ref={this.wrapRef}>
        {label && (
          <label className={inputStyles.label}>
            {label}
            {required && <span className={inputStyles.required}>*</span>}
          </label>
        )}
        <div
          className={[styles.box, styles[size], open ? styles.boxOpen : '', error ? styles.boxError : ''].join(' ')}
          onClick={this.toggle}
        >
          <span className={selected ? styles.value : styles.placeholder}>
            {selected ? selected.label : (placeholder || 'Select…')}
          </span>
          <Icon name={open ? 'expand_less' : 'expand_more'} size={16} className={styles.arrow} />
        </div>
        {hint && !error && <div className={inputStyles.hint}>{hint}</div>}
        {error && <div className={inputStyles.err}>{error}</div>}

        {open && coords && ReactDOM.createPortal(
          <div
            ref={(el) => { this.menuRef = el; }}
            className={styles.menu}
            style={{ position: 'fixed', top: coords.top, left: coords.left, width: coords.width }}
          >
            <div className={styles.searchWrap}>
              <Icon name="search" size={14} className={styles.searchIcon} />
              <input
                ref={this.searchRef}
                className={styles.searchInput}
                placeholder="Search…"
                value={query}
                onChange={this.handleSearchChange}
                onKeyDown={this.handleSearchKeyDown}
              />
            </div>
            <div className={styles.optionsList}>
              {filtered.length === 0 && <div className={styles.empty}>No matches</div>}
              {filtered.map((opt, i) => (
                <div
                  key={opt.value}
                  className={[
                    styles.option,
                    opt.value === value ? styles.optionActive : '',
                    i === highlighted ? styles.optionHighlighted : '',
                    opt.disabled ? styles.optionDisabled : '',
                  ].join(' ')}
                  onMouseEnter={() => this.setState({ highlighted: i })}
                  onClick={() => this.select(opt)}
                >
                  <span>{opt.label}</span>
                  {opt.value === value && <Icon name="check" size={14} />}
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  }
}

export default Select;
